import { FC } from 'react';
import { Collapse, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import PolicyAlert from '@acorn-portal/shared/src/components/alerts/Alert';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { MileageSummaryDto } from '@acorn-portal/shared/src/services/telematicsMileageApi';
import { getMileageAlertType, getAlertContent } from './mileage-utils';

/**
 * Props for the MileageAlerts component.
 */
interface MileageAlertsProps {
	/** Mileage summary data from the telematics API. */
	mileageSummary: MileageSummaryDto | null | undefined;
}

/**
 * Mileage warning alerts component for telematics policy details.
 *
 * Displays contextual banners based on the driver's mileage usage:
 * - Exceeded (danger): Driver has already gone over their mileage allowance
 * - Predicted to exceed (warning): System calculated predicted excess; shows (x) miles
 * - Due to exceed (warning): Running low on annual mileage
 *
 * Follows the same design pattern as PayNowAlerts with a PolicyAlert component
 * and a "Top Up Miles" action button.
 *
 * @component
 * @example
 * ```tsx
 * <MileageAlerts mileageSummary={summary} />
 * ```
 */
const MileageAlerts: FC<MileageAlertsProps> = ({ mileageSummary }) => {
	const navigate = useNavigate();

	const { alertType, isCancellable } = getMileageAlertType(mileageSummary);

	if (!alertType) return null;

	const predictedExcessMiles = mileageSummary?.predictedExcessMiles;
	const { title, message, severity } = getAlertContent(alertType, predictedExcessMiles, isCancellable);

	const handleTopUpClick = () => {
		navigate(ROUTE_PATHS.topUpMilesDate);
	};

	return (
		<Collapse in>
			<PolicyAlert type={severity} title={title} onActionClick={handleTopUpClick} actionText='Top Up Miles'>
				<Typography>{message}</Typography>
			</PolicyAlert>
		</Collapse>
	);
};

export default MileageAlerts;
